// import packages or dependencies
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import axiosInstance from "../../../configs/axios/axios ";
import {
  USERMANAGEMENT_LIST,
  DELETE_USERMANAGEMENT_LIST,
  CREATE_USERMANAGEMENT,
  UPDATE_USERMANAGEMENT,
  UPDATE_USERMANAGEMENT_ADMIN,
} from "../../../configs/axios/constantsAPI";
import { getCookie } from "../../../configs/cookie-helper/cookieHelper";

/**
 * This thunk receives users list table data
 */
export const getUserManagementListAction = createAsyncThunk(
  "userManagement/getUserManagementListAction",
  async (_, { rejectWithValue, getState }: any) => {
    // get token from cookie
    let token = getCookie("token") as string;

    try {
      const { data } = await axiosInstance.post(USERMANAGEMENT_LIST, _, {
        headers: {
          token: token ? token : getState().authenticationState.token,
        },
      });
      return data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * This thunk creates a new user in user-management page.
 */
export const createUserManagementUserAction = createAsyncThunk(
  "userManagement/createUserManagementUserAction",
  async (sendData: any, { dispatch, rejectWithValue, getState }: any) => {
    // get token from cookie
    let token = getCookie("token") as string;

    try {
      const { data } = await axiosInstance.post(
        CREATE_USERMANAGEMENT,
        JSON.stringify(sendData),
        {
          headers: {
            token: token ? token : getState().authenticationState.token,
          },
        }
      );

      dispatch(getUserManagementListAction());
      return data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * This thunk updates the target user in user-management page
 */
export const updateUserManagementUserAction = createAsyncThunk(
  "userManagement/updateUserManagementUserAction",
  async (sendData: any, { dispatch, rejectWithValue, getState }: any) => {
    // get token from cookie
    let token = getCookie("token") as string;

    try {
      const { data } = await axiosInstance.post(
        UPDATE_USERMANAGEMENT,
        JSON.stringify(sendData),
        {
          headers: {
            token: token ? token : getState().authenticationState.token,
          },
        }
      );

      dispatch(getUserManagementListAction());
      return data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * This thunk updates the target admin in user-management page
 */
export const updateUserManagementAdminAction = createAsyncThunk(
  "userManagement/updateUserManagementAdminAction",
  async (sendData: any, { dispatch, rejectWithValue, getState }: any) => {
    // get token from cookie
    let token = getCookie("token") as string;

    try {
      const { data } = await axiosInstance.post(
        UPDATE_USERMANAGEMENT_ADMIN,
        JSON.stringify(sendData),
        {
          headers: {
            token: token ? token : getState().authenticationState.token,
          },
        }
      );

      dispatch(getUserManagementListAction());
      return data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * This thunk deletes the target user in user-management page
 */
export const deleteUserManagementListAction = createAsyncThunk(
  "userManagement/deleteUserManagementListAction",
  async (sendData: any, { dispatch, rejectWithValue, getState }: any) => {
    // get token from cookie
    let token = getCookie("token") as string;

    try {
      const { data } = await axiosInstance.post(
        DELETE_USERMANAGEMENT_LIST,
        JSON.stringify(sendData),
        {
          headers: {
            token: token ? token : getState().authenticationState.token,
          },
        }
      );

      dispatch(getUserManagementListAction());
      return data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * User data structure
 */
export interface Items {
  id: string;
  nickname: string;
  username: string;
  rule: string;
  state: string;
}

/**
 * User-management data structure
 */
export interface UserManagementState {
  userManagementData: {
    actionStatus: string;
    alert: {
      error: boolean;
      message: string;
    };
    list: Array<Items>;
  };

  createUser: {
    actionStatus: string;
    alert: {
      error: boolean;
      message: string;
    };
    type: "update" | "create";
    userId: string;
    formIsOpen: boolean;
    formData: {
      nickname: string;
      username: string;
      rule: "user" | "admin";
      password: string;
      passwordConf: string;
      oldPass: string;
    };
  };
}

/**
 * Initial user-management data structure instance
 */
const initialState: UserManagementState = {
  userManagementData: {
    actionStatus: "",
    alert: {
      error: false,
      message: "",
    },
    list: [],
  },
  createUser: {
    actionStatus: "",
    alert: {
      error: false,
      message: "",
    },
    type: "create",
    userId: "",
    formIsOpen: false,
    formData: {
      nickname: "",
      username: "",
      rule: "user",
      password: "",
      passwordConf: "",
      oldPass: "",
    },
  },
};

/**
 * User-management slice object
 */
export const userManagementSlice = createSlice({
  name: "userManagement",
  initialState,
  reducers: {
    /**
     * It resets and clears the user form.
     * @param {string} state - The state of user-management slice.
     */
    resetCreateUserAction: (state) => {
      state.createUser.actionStatus = initialState.createUser.actionStatus;
      state.createUser.alert = initialState.createUser.alert;
      state.createUser.type = initialState.createUser.type;
      state.createUser.formIsOpen = initialState.createUser.formIsOpen;
      state.createUser.formData = initialState.createUser.formData;
      state.createUser.userId = initialState.createUser.userId;
    },
    /**
     * Represents an action object.
     * @typedef {Object} Action
     * @property {boolean} formIsOpen - Indicates if the form is open.
     * @property {string} type - Type of action.
     */
    /**
     * Opens create user form when user clicks '+Create User' button.
     * @param {string} state - The state of user-management slice.
     * @param {Action} action - The action of openCreateaUserFormAction.
     */
    openCreateaUserFormAction: (state, action) => {
      state.createUser.formIsOpen = action.payload.formIsOpen;
      state.createUser.type = action.payload.type;
      state.createUser.userId = initialState.createUser.userId;
    },
    /**
     * Represents an action object.
     * @typedef {Object} Action
     * @property {boolean} formIsOpen - Indicates if the form is open.
     * @property {string} type - Type of action.
     * @property {string} userId - Indicates if the form is open.
     * @property {object} formData - All the information in the table related to the target user.
     */
    /**
     * Opens update user form when user clicks the row of table in.
     * @param {string} state - The state of user-management slice.
     * @param {Action} action - The action of editFormSetFormAction.
     */
    editFormSetFormAction: (state, action) => {
      state.createUser.formIsOpen = action.payload.formIsOpen;
      state.createUser.type = action.payload.type;
      state.createUser.userId = action.payload.userId;
      state.createUser.formData = action.payload.formData;
    },
  },
  extraReducers: ({ addCase }) => {
    addCase(getUserManagementListAction.pending, (state) => {
      state.userManagementData.actionStatus = "loading";
      state.userManagementData.alert = {
        error: false,
        message: "",
      };
    });
    addCase(getUserManagementListAction.fulfilled, (state, { payload }) => {
      state.userManagementData.actionStatus = "success";
      state.userManagementData.alert = {
        error: payload.content?.error,
        message: payload.content?.error
          ? payload.content?.message === "login_again"
            ? "login_again"
            : payload.content?.message
          : "Great, we didn't encounter any errors!",
      };
      state.userManagementData.list = payload?.content?.data
        ? payload.content?.data
        : [];
    });
    addCase(getUserManagementListAction.rejected, (state, { payload }: any) => {
      state.userManagementData.actionStatus = "rejected";
      state.userManagementData.alert = {
        error: payload.content?.error,
        message:
          payload.content?.message === "not_access"
            ? "not_access"
            : payload.content?.message,
      };
    });

    addCase(createUserManagementUserAction.pending, (state) => {
      state.createUser.actionStatus = "loading";
      state.createUser.alert = {
        error: false,
        message: "",
      };
    });
    addCase(createUserManagementUserAction.fulfilled, (state, { payload }) => {
      state.createUser.actionStatus = "success";
      state.createUser.alert = {
        error: payload.content?.error,
        message:
          payload.content?.message === "username_not_unique"
            ? "username_not_unique"
            : payload.content?.message,
      };
    });
    addCase(
      createUserManagementUserAction.rejected,
      (state, { payload }: any) => {
        state.createUser.actionStatus = "rejected";
        state.createUser.alert = {
          error: payload.content?.error,
          message: payload.content?.error
            ? "Sorry, we encountered an error!"
            : "Great, we didn't encounter any errors!",
        };
      }
    );

    addCase(updateUserManagementUserAction.pending, (state) => {
      state.createUser.actionStatus = "loading";
      state.createUser.alert = {
        error: false,
        message: "",
      };
    });
    addCase(updateUserManagementUserAction.fulfilled, (state, { payload }) => {
      state.createUser.actionStatus = "success";
      state.createUser.alert = {
        error: payload.content?.error,
        message: payload.content?.error
          ? "Sorry, we encountered an error!"
          : "User updated successfuly.",
      };
    });
    addCase(
      updateUserManagementUserAction.rejected,
      (state, { payload }: any) => {
        state.createUser.actionStatus = "rejected";
        state.createUser.alert = {
          error: payload.content?.error,
          message:
            payload.content?.message === "username_not_unique"
              ? "username_not_unique"
              : payload.content?.message,
        };
      }
    );

    addCase(updateUserManagementAdminAction.pending, (state) => {
      state.createUser.actionStatus = "loading";
      state.createUser.alert = {
        error: false,
        message: "",
      };
    });
    addCase(updateUserManagementAdminAction.fulfilled, (state, { payload }) => {
      state.createUser.actionStatus = "success";
      state.createUser.alert = {
        error: payload.content?.error,
        message: payload.content?.error
          ? "Sorry, we encountered an error!"
          : "User updated successfuly.",
      };
    });
    addCase(
      updateUserManagementAdminAction.rejected,
      (state, { payload }: any) => {
        state.createUser.actionStatus = "rejected";
        state.createUser.alert = {
          error: payload.content?.error,
          message: payload.content?.message,
        };
      }
    );

    addCase(deleteUserManagementListAction.pending, (state) => {
      state.createUser.actionStatus = "loading";
      state.createUser.alert = {
        error: false,
        message: "",
      };
    });
    addCase(deleteUserManagementListAction.fulfilled, (state, { payload }) => {
      state.createUser.actionStatus = "success";
      state.createUser.alert = {
        error: payload.content?.error,
        message: payload.content?.error
          ? "Sorry, we encountered an error in delete method!"
          : "User deleted successfuly.",
      };
    });
    addCase(
      deleteUserManagementListAction.rejected,
      (state, { payload }: any) => {
        state.createUser.actionStatus = "rejected";
        state.createUser.alert = {
          error: payload.content?.error,
          message: payload.content?.message,
        };
      }
    );
  },
});

/**
 * Exporting actions & reducers
 */
export const {
  resetCreateUserAction,
  openCreateaUserFormAction,
  editFormSetFormAction,
} = userManagementSlice.actions;

/**
 * Export user-management redux state
 */
export const userManagement = (state: RootState) => state.userManagementState;

/**
 * Export user-management reducer for use in store of redux
 */
export default userManagementSlice.reducer;
